/**
 * Shared ECharts configuration, theme, and Indian-format helpers for all report charts.
 * Amounts use full en-IN comma grouping (no K / L / Cr compact suffixes).
 */
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

const DECIMAL_GROUPED = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Parse compact suffix amounts from API strings (e.g. 10L, 1.2Cr, 5 Lac). */
function tryParseCompactAmount(text) {
  const s = String(text || '').trim().replaceAll(',', '');
  const m = /^(-?\d+(?:\.\d+)?)\s*(lac|lakh|crore|cr|l|k)?$/i.exec(s);
  if (!m) return null;
  const num = Number.parseFloat(m[1]);
  if (!Number.isFinite(num)) return null;
  const suffix = (m[2] || '').toLowerCase();
  if (suffix === 'cr' || suffix === 'crore') return num * 1_00_00_000;
  if (suffix === 'l' || suffix === 'lac' || suffix === 'lakh') return num * 1_00_000;
  if (suffix === 'k') return num * 1_000;
  return num;
}

function stripCurrencySymbol(text) {
  const symbol = getCurrencySymbol();
  const escapedSymbol = symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return String(text || '')
    .replace(new RegExp(`^${escapedSymbol}\\s*`, 'i'), '')
    .replace(/^₹\s*/i, '')
    .replace(/^(?:rs\.?|inr)\s*/i, '')
    .trim();
}

/** Parse a number or money string (currency symbol, commas, compact suffixes) into a number. */
export function parseNumericMoneyInput(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

  const str = String(raw).trim();
  if (!str) return null;

  const compact = tryParseCompactAmount(stripCurrencySymbol(str));
  if (compact != null) return compact;

  const cleaned = stripCurrencySymbol(str).replaceAll(',', '').trim();
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Format a number with Indian comma grouping (no currency symbol). */
export function formatIndianAuto(raw) {
  const n = parseNumericMoneyInput(raw);
  if (n == null) return '0';
  return DECIMAL_GROUPED.format(n);
}

/** Format as full currency with comma grouping (no K / L / Cr). */
export function formatRupeesIndianCompact(raw) {
  const n = parseNumericMoneyInput(raw);
  if (n == null) return `${getCurrencySymbol()}0`;
  return `${getCurrencySymbol()}${DECIMAL_GROUPED.format(n)}`;
}

/** Replace compact money tokens inside a larger string. */
export function formatMoneyTokensInText(text) {
  if (text == null || text === '') return '—';
  const str = String(text);
  const symbol = getCurrencySymbol();
  const escapedSymbol = symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return str.replace(new RegExp(`(?:${escapedSymbol}|₹|rs\\.?|inr)\\s*[\\d,.]+\\s*(?:lac|lakh|crore|cr|l|k)?`, 'gi'), token => {
    const formatted = formatRupeesIndianCompact(token);
    return formatted === `${symbol}0` ? token : formatted;
  });
}

/**
 * Premium tooltip config shared across all ECharts report charts.
 */
export const premiumTooltip = {
  trigger: 'axis',
  backgroundColor: 'rgba(15, 23, 42, 0.92)',
  borderColor: 'rgba(148, 163, 184, 0.2)',
  borderWidth: 1,
  padding: [10, 14],
  textStyle: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: 11,
    color: '#e2e8f0',
  },
  axisPointer: {
    type: 'shadow',
    shadowStyle: { color: 'rgba(148, 163, 184, 0.08)' },
  },
  extraCssText: 'border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15);',
};

/**
 * Shared x-axis / y-axis label style.
 */
export const axisLabelStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 10,
  color: '#6B7280',
};

/**
 * Shared grid config for standard report charts (compact padding).
 */
export const standardGrid = {
  left: 36,
  right: 18,
  top: 24,
  bottom: 28,
  containLabel: true,
};

export const horizontalGrid = {
  left: 8,
  right: 18,
  top: 24,
  bottom: 12,
  containLabel: true,
};

/**
 * Format the y-axis (value axis) with Indian notation.
 */
export const indianValueAxis = {
  type: 'value',
  axisLabel: {
    ...axisLabelStyle,
    formatter: (value) => formatIndianAuto(value),
  },
  splitLine: {
    lineStyle: { color: '#EEF2F7', type: 'dashed' },
  },
  axisLine: { show: false },
  axisTick: { show: false },
};

/**
 * Standard category axis configuration.
 */
export const indianCategoryAxis = {
  type: 'category',
  axisLabel: {
    ...axisLabelStyle,
    interval: 0,
    rotate: 0,
  },
  axisLine: { lineStyle: { color: '#E5E7EB' } },
  axisTick: { show: false },
};

/**
 * Standard tooltip formatter for stacked bar charts (Indian formatting).
 */
export function stackedBarTooltipFormatter(params) {
  if (!Array.isArray(params) || params.length === 0) return '';
  const title = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${params[0].axisValueLabel}</div>`;
  const rows = params
    .filter(p => p.value != null && p.value !== 0)
    .map(p => {
      const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>`;
      return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${marker}<span style="flex:1">${p.seriesName}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(p.value)}</span></div>`;
    })
    .join('');
  return title + rows;
}

/**
 * Single-series tooltip formatter.
 */
export function singleBarTooltipFormatter(params) {
  const p = Array.isArray(params) ? params[0] : params;
  if (!p) return '';
  return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name || p.axisValueLabel || ''}</div><div style="font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(p.value)}</div>`;
}

/**
 * Pie chart tooltip formatter.
 */
export function pieTooltipFormatter(params) {
  const p = Array.isArray(params) ? params[0] : params;
  if (!p) return '';
  const pct = typeof p.percent === 'number' ? p.percent.toFixed(1) : '0';
  return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div><div style="font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(p.value)} (${pct}%)</div>`;
}

/**
 * Default color palette for status-based series.
 */
export const STATUS_COLORS = {
  confirmed: '#66BB6A',
  tentative: '#FFC107',
  waitlisted: '#42A5F5',
  /** Distinct from tentative (amber); used for payment-dues segments in dashboards. */
  paymentDue: '#FDD835',
  cancelled: '#E57373',
  actualized: '#9575CD',
  rfq: '#FFA726',
  budget: '#78909C',
  assigned: '#78909C',
  complementary: '#4DB6AC',
};

/**
 * Time-slot palette (avoids status-color conflict).
 */
export const SLOT_COLORS = {
  Breakfast: '#80DEEA',
  Lunch: '#FFD54F',
  'Hi-tea': '#A1887F',
  Dinner: '#7986CB',
  'Full Day': '#90A4AE',
  'Morning Conference': '#80CBC4',
};

/**
 * Default animation settings for subtle entrance.
 */
export const defaultAnimation = {
  animationDuration: 600,
  animationEasing: 'cubicOut',
};
